/* eslint-disable @next/next/no-img-element */
import { observer } from "mobx-react"
import { BreakPoints } from "styles/styles"
import { useStore } from "@features/common"
import { CareersBenefit } from "@serializers/careers-benefit"
import parse from "react-html-parser"
import { useEffect, useRef, useState } from "react"

import BenefitsBgMobile from "./benefits-bg-mobile.png"
import TopBgMobile from "./top-bg-mobile.svg"
import BottomBgMobile from "./bottom-bg-mobile.svg"
import Popcorn from "./popcorn.png"
import NoteBg from "./note-bg.png"
import NoteBgDesktop from "./note-bg-desktop.png"
import Top from "./top.svg"
import Bottom from "./bottom.svg"
import Left from "./left.svg"
import Right from "./right.svg"
import Pin from "./pin.png"
import ArrowRight from "./arrow-right.png"
import ArrowLeft from "./arrow-left.png"
import Star from "./star.png"
import StarSecond from "./star-second.png"

// The layout of each benefit card, not its words or its picture. Both of those
// come from the benefits list in Strapi, matched on the key each entry carries,
// so an editor can reorder that list without a card changing frame.
//
// The card frames themselves are bespoke JSX below. Nothing here but the key and
// the two fallbacks is read: a card's angles and offsets live in its own markup.
//
// There is no bundled illustration any more: a card with no upload renders its
// words without a picture.
//
// fallbackTitle and fallbackDescription belong to the older flat template keys
// and are only reached through the legacy path below. They go away with those
// keys.
const BENEFIT_LAYOUTS = [
  {
    key: "unforgettableTrips",
    fallbackTitle: "Unforgettable trips",
    fallbackDescription:
      "Once a year, we turn off our laptops and turn on pure holiday mode. Think 5* hotels, amazing food, and parties. No strategy workshops, a real vacation with your colleagues, all on us",
  },
  {
    key: "workFromAnywhere",
    fallbackTitle: "Work from anywhere",
    fallbackDescription:
      "We’re a fully remote team, giving you the freedom to work from your happy place, wherever that may be.",
  },
  {
    key: "berlinOnDemand",
    fallbackTitle: "Berlin on demand",
    fallbackDescription:
      "Want some face time with your team? Most employees can come hang at our Berlin office during office weeks. We’ll cover your flight and hotel.",
  },
  {
    key: "alwaysLearning",
    fallbackTitle: "Always be learning",
    fallbackDescription:
      "Get a personal budget for courses and conferences. If it helps you grow, we’re here to support you.",
  },
  {
    key: "greatPay",
    fallbackTitle: "Great pay for great work",
    fallbackDescription:
      "We offer a competitive salary and bonuses based on your position and experience. And we do an annual review of salaries based on performance reviews, so good results are rewarded.",
  },
  {
    key: "profitable",
    fallbackTitle: "Profitable and stable",
    fallbackDescription:
      "Profitable and stable. You’ll be working for a highly profitable, $100M+ revenue company. This gives us the stability to keep innovating and scaling Pure to the next level.",
  },
]

// Every card shows its illustration the same way: only when Strapi holds one,
// since nothing is bundled with the frontend any more. The frame around it is
// bespoke per card, so the class list stays with the card.
type CardImageProps = {
  card: { image: string }
  alt: string
  className: string
}

const CardImage = (props: CardImageProps) => {
  const { card, alt, className } = props

  if (!card.image) return null

  return <img src={card.image} alt={alt} className={className} />
}

const Benefits = () => {
  const {
    ResponsiveModel: { width },
    VacanciesModel: { getTemplate, benefits: strapiBenefits },
  } = useStore()
  const isMobile = width > 0 && width < BreakPoints.tablet
  const isTabletUp = width >= BreakPoints.tablet

  const noteTextRef = useRef<HTMLDivElement | null>(null)
  const [noteBgHeight, setNoteBgHeight] = useState(() =>
    isTabletUp ? 262 : 216,
  )

  const heading = parse(
    getTemplate("careers.benefits.title", false) ||
      '<span class="text-red-300 line-through">Employee Benefits</span><br/>The good stuff',
  )

  // Each card takes its words from the Strapi entry that carries its key, and
  // from the older flat template keys for anything that entry leaves empty, so
  // the page keeps working while the content moves across. Matching on the key
  // rather than the position means reordering the list in the admin does not put
  // a benefit inside another one's card frame.
  const benefitCards = BENEFIT_LAYOUTS.map((layout) => {
    const content: CareersBenefit | undefined = strapiBenefits.find(
      (benefit) => benefit.key === layout.key,
    )

    return {
      image: content?.image ?? "",
      title:
        content?.title ||
        getTemplate(`careers.benefits.${layout.key}.title`) ||
        layout.fallbackTitle,
      description:
        content?.description ||
        getTemplate(`careers.benefits.${layout.key}.description`) ||
        layout.fallbackDescription,
    }
  })

  const noteHeading =
    getTemplate("careers.benefits.note.heading") || "A quick note:"
  const noteText =
    getTemplate("careers.benefits.note.description") ||
    "All our social events are invitations, not obligations. You’d prefer Pure as just your remote job? We love that for you. No judgment."

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const textHeight = noteTextRef.current?.scrollHeight ?? 0
      const minHeight = isTabletUp ? 262 : 216
      const topFraction = isTabletUp ? 0.25 : 0.16
      const extraPx = isTabletUp ? 36 : 28

      const neededHeight = Math.ceil((textHeight + extraPx) / (1 - topFraction))

      setNoteBgHeight(Math.max(minHeight, neededHeight))
    })

    return () => cancelAnimationFrame(frame)
  }, [isTabletUp, width, noteHeading, noteText])

  return (
    <>
      <section
        className="relative flex flex-col items-center bg-no-repeat bg-cover bg-center mt-[240px] tablet:mt-0"
        style={{
          backgroundImage: `url(${
            isMobile ? BenefitsBgMobile.src : BenefitsBgMobile.src
          })`,
        }}
      >
        <TopBgMobile
          viewBox="0 0 525 163"
          className="absolute top-[-1px] left-0 w-full pointer-events-none select-none"
        />
        <div className="relative z-1 flex justify-center gap-[20px]">
          <img
            src={Popcorn.src}
            alt="Popcorn"
            className="hidden tablet:block relative bottom-[50px] desktop:bottom-[100px] z-1 desktop:ml-[53px] w-auto h-full max-h-[300px] desktop:max-h-[387px]"
          />
          <img
            src={Popcorn.src}
            alt="Popcorn"
            className="absolute top-[-180px] mr-[100px] block tablet:hidden w-auto h-full max-h-[230px]"
          />

          <div className="overflow-hidden">
            <h2 className="px-[25px] pt-[55px] tablet:pt-[30px] w-fit h-fit tablet:mb-[100px] mb-[50px] font-quirk-bold text-[32px] tablet:text-[64px] leading-[40px] tablet:leading-[70px] tracking-[1px] tablet:tracking-[4px] uppercase rotate-[-6.45deg]">
              {heading}
            </h2>
          </div>
        </div>
        <div className="px-[38px] w-full max-w-screen-desktop-l mx-auto">
          {/* row */}
          <div className="mt-[50px] laptop-lg:mt-0 mb-[120px] laptop-lg:mb-[100px] w-full flex justify-center flex-col-reverse gap-[120px] laptop-lg:gap-0 laptop-lg:flex-row items-center laptop-lg:items-start">
            <div className="relative laptop-lg:top-[50px] bg-white max-w-[515px] rotate-[3deg] tablet:rotate-[-8deg]">
              <Left
                viewBox="0 0 15 456"
                className="absolute top-0 left-[-10px] h-full pointer-events-none select-none"
              />
              <Right
                viewBox="0 0 15 456"
                className="absolute top-0 right-[-10px] h-full pointer-events-none select-none"
              />
              <Top
                viewBox="0 0 462 17"
                className="absolute top-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <Bottom
                viewBox="0 0 462 17"
                className="absolute bottom-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <img
                src={Pin.src}
                alt="Pin"
                className="rotate-[32.45deg] absolute top-[-20px] left-5 laptop-lg:left-auto laptop-lg:right-4 w-auto h-full max-h-[50px] laptop-lg:max-h-[75px] pointer-events-none select-none"
              />
              <CardImage
                card={benefitCards[0]}
                alt="Trips Illustration"
                className="absolute top-[-80px] laptop-lg:top-[-40px] right-0 laptop-lg:left-[5%] w-auto h-full max-h-[150px] laptop-lg:max-h-[230px]"
              />
              <div className="w-full px-[25px] laptop-lg:px-[72px] pb-[25px] laptop-lg:pb-[48px] pt-[80px] laptop-lg:pt-[200px]">
                <div className="w-full mx-auto">
                  <p className="w-fit mb-[20px] font-sans-bold leading-[18px] laptop:leading-[38px] text-[13px] laptop:text-[24px] uppercase">
                    {benefitCards[0].title}
                  </p>
                  <p className="w-fit font-sans text-[16px] leading-[20px] laptop:text-[20px] laptop:leading-[32px]">
                    {benefitCards[0].description}
                  </p>
                </div>
              </div>
            </div>
            <img
              src={ArrowLeft.src}
              alt="Arrow Left"
              className="relative top-[-50px] h-[80px] right-[20px] hidden laptop-lg:block"
            />
            <div className="relative bg-white max-w-[515px] rotate-[-4deg] laptop-lg:rotate-[8deg]">
              <Left
                viewBox="0 0 15 456"
                className="absolute top-0 left-[-14px] h-full pointer-events-none select-none"
              />
              <Right
                viewBox="0 0 15 456"
                className="absolute top-0 right-[-14px] h-full pointer-events-none select-none"
              />
              <Top
                viewBox="0 0 462 17"
                className="absolute top-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <Bottom
                viewBox="0 0 462 17"
                className="absolute bottom-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <img
                src={Pin.src}
                alt="Pin"
                className="rotate-[55deg] absolute top-[-10px] laptop-lg:top-[10px] right-[5%] laptop-lg:right-[-10px] w-auto h-full max-h-[50px] laptop-lg:max-h-[75px] pointer-events-none select-none"
              />
              <div className="w-full pt-[80px] laptop-lg:pt-[48px] px-[25px] laptop-lg:px-[48px] pb-[25px] laptop-lg:pb-[48px]">
                <CardImage
                  card={benefitCards[1]}
                  alt="WFA Illustration"
                  className="absolute top-[-50px] left-[10%] laptop-lg:left-0 laptop-lg:top-[-20px] laptop-lg:relative rotate-[-7deg] w-auto h-full max-h-[120px] laptop-lg:max-h-[173px]"
                />
                <div className="w-full px-0 laptop-lg:px-[40px]">
                  <p className="mb-[20px] w-fit font-sans-bold leading-[18px] laptop:leading-[38px] text-[13px] laptop:text-[24px] uppercase">
                    {benefitCards[1].title}
                  </p>
                  <p className="w-fit font-sans text-[16px] leading-[20px] laptop:text-[20px] laptop:leading-[32px]">
                    {benefitCards[1].description}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* row */}
          <div className="mb-[120px] laptop-lg:mb-[100px] w-full flex justify-center gap-[120px] laptop-lg:gap-[100px] items-center flex-col laptop-lg:flex-row">
            <div className="relative laptop-lg:top-[50px] bg-white max-w-[515px] rotate-[-3deg] tablet:rotate-[8deg]">
              <Left
                viewBox="0 0 15 456"
                className="absolute top-0 left-[-14px] h-full pointer-events-none select-none"
              />
              <Right
                viewBox="0 0 15 456"
                className="absolute top-0 right-[-14px] h-full pointer-events-none select-none"
              />
              <Top
                viewBox="0 0 462 17"
                className="absolute top-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <Bottom
                viewBox="0 0 462 17"
                className="absolute bottom-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <img
                src={Pin.src}
                alt="Pin"
                className="rotate-[35deg] absolute top-[-10px] laptop-lg:top-[-20px] right-7 w-auto h-full max-h-[50px] laptop-lg:max-h-[75px] pointer-events-none select-none"
              />
              <CardImage
                card={benefitCards[2]}
                alt="On Demand Illustration"
                className="mb-[20px] absolute top-[-80px] laptop-lg:top-[-30px] left-[10%] w-auto h-full max-h-[130px] laptop-lg:max-h-[220px]"
              />
              <div className="w-full px-[25px] laptop-lg:px-[72px] pb-[25px] laptop-lg:pb-[48px] pt-[70px] laptop-lg:pt-[220px]">
                <div className="w-full mx-auto">
                  <p className="mb-[20px] w-fit font-sans-bold leading-[18px] laptop:leading-[38px] text-[13px] laptop:text-[24px] uppercase">
                    {benefitCards[2].title}
                  </p>
                  <p className="w-fit font-sans text-[16px] leading-[20px] laptop:text-[20px] laptop:leading-[32px]">
                    {benefitCards[2].description}
                  </p>
                </div>
              </div>
            </div>

            <div className="relative bg-white max-w-[515px] laptop-lg:px-[40px] laptop-lg:pb-[48px] rotate-[3deg] laptop-lg:rotate-[-4deg]">
              <Left
                viewBox="0 0 15 456"
                className="absolute top-0 left-[-14px] h-full pointer-events-none select-none"
              />
              <Right
                viewBox="0 0 15 456"
                className="absolute top-0 right-[-14px] h-full pointer-events-none select-none"
              />
              <Top
                viewBox="0 0 462 17"
                className="absolute top-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <Bottom
                viewBox="0 0 462 17"
                className="absolute bottom-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <img
                src={Pin.src}
                alt="Pin"
                className="rotate-[-5deg] tablet-lg:rotate-[15deg] absolute top-[-10px] laptop-lg:top-[-20px] right-[3%] left-[3%] laptop-lg:left-auto laptop-lg:right-12 w-auto h-full max-h-[50px] laptop-lg:max-h-[75px] pointer-events-none select-none"
              />
              <div className="w-full px-[25px] laptop-lg:px-0 pb-[25px] laptop-lg:pb-0 pt-[70px] laptop-lg:pt-0 ">
                <CardImage
                  card={benefitCards[3]}
                  alt="Always Be Learning Illustration"
                  className="absolute top-[-80px] right-[10%] laptop-lg:static mb-[20px] w-auto h-full max-h-[125px] laptop-lg:max-h-[214px]"
                />
                <div className="w-full px-0 laptop-lg:px-[32px]">
                  <p className="w-fit mb-[20px] font-sans-bold leading-[18px] laptop:leading-[38px] text-[13px] laptop:text-[24px] uppercase">
                    {benefitCards[3].title}
                  </p>
                  <p className="w-fit font-sans text-[16px] leading-[20px] laptop:text-[20px] laptop:leading-[32px]">
                    {benefitCards[3].description}
                  </p>
                </div>
              </div>
              <img
                src={Star.src}
                alt="Star"
                className="absolute top-[0px] right-[-110px] w-auto h-full max-h-[72px] hidden laptop-lg:block pointer-events-none select-none"
              />
            </div>
          </div>

          {/* row */}
          <div className="mb-[100px] laptop-lg:mb-[200px] w-full flex justify-center flex-col gap-[120px] laptop-lg:gap-0 laptop-lg:flex-row items-center laptop-lg:items-start">
            <div className="relative laptop-lg:top-[100px] bg-white max-w-[515px] rotate-[-3deg] laptop-lg:rotate-[-4deg]">
              <Left
                viewBox="0 0 15 456"
                className="absolute top-0 left-[-10px] h-full pointer-events-none select-none"
              />
              <Right
                viewBox="0 0 15 456"
                className="absolute top-0 right-[-10px] h-full pointer-events-none select-none"
              />
              <Top
                viewBox="0 0 462 17"
                className="absolute top-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <Bottom
                viewBox="0 0 462 17"
                className="absolute bottom-[-10px] w-full pointer-events-none select-none"
              />
              <img
                src={Pin.src}
                alt="Pin"
                className="absolute top-[-10px] laptop-lg:top-[-20px] right-6 laptop-lg:right-12 w-auto h-full max-h-[50px] laptop-lg:max-h-[75px] pointer-events-none select-none"
              />
              <CardImage
                card={benefitCards[4]}
                alt="On Demand Illustration"
                className="mb-[20px] absolute top-[-80px] laptop-lg:top-[-135px] left-[7%] w-auto h-full max-h-[165px] laptop-lg:max-h-[270px]"
              />
              <img
                src={StarSecond.src}
                alt="StarSecond"
                className="absolute top-[-100px] left-[-4%] w-auto max-h-[48px] hidden laptop-lg:block pointer-events-none select-none"
              />
              <div className="w-full px-[25px] laptop-lg:px-[40px] pt-[100px] laptop-lg:pt-[160px] pb-[25px] laptop-lg:pb-[50px]">
                <div className="w-full px-0 laptop-lg:px-[32px] mx-auto">
                  <p className="w-fit mb-[20px] font-sans-bold leading-[18px] laptop:leading-[38px] text-[13px] laptop:text-[24px] uppercase">
                    {benefitCards[4].title}
                  </p>
                  <p className="w-fit font-sans text-[16px] leading-[20px] laptop:text-[20px] laptop:leading-[32px]">
                    {benefitCards[4].description}
                  </p>
                </div>
              </div>
            </div>
            <img
              src={ArrowRight.src}
              alt="Arrow"
              className="relative left-[20px] top-[70px] h-[100px] hidden laptop-lg:block"
            />
            <div className="relative laptop-lg:left-10 bg-white max-w-[515px] rotate-[3deg] laptop-lg:rotate-[4deg]">
              <Left
                viewBox="0 0 15 456"
                className="absolute top-0 left-[-14px] h-full pointer-events-none select-none"
              />
              <Right className="absolute top-0 right-[-14px] h-full pointer-events-none select-none" />
              <Top
                viewBox="0 0 462 17"
                className="absolute top-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <Bottom
                viewBox="0 0 462 17"
                className="absolute bottom-[-16px] left-0 w-full pointer-events-none select-none"
              />
              <CardImage
                card={benefitCards[5]}
                alt="Always Be Learning Illustration"
                className="absolute top-[-80px] laptop-lg:top-[-40px] right-3 laptop-lg:left-[12%] w-auto h-full max-h-[135px] laptop-lg:max-h-[224px]"
              />
              <img
                src={Pin.src}
                alt="Pin"
                className="absolute rotate-[60deg] top-[-10px] laptop-lg:top-[-20px] left-5 laptop-lg:left-[auto] right-[unset] laptop-lg:right-12 w-auto h-full max-h-[50px] laptop-lg:max-h-[75px] pointer-events-none select-none"
              />
              <div className="w-full px-[25px] laptop-lg:px-[72px] pb-[25px] laptop-lg:pb-[48px] pt-[80px] laptop-lg:pt-[220px]">
                <div className="w-[100%] mx-auto">
                  <p className="w-fit mb-[20px] font-sans-bold leading-[18px] laptop:leading-[38px] text-[13px] laptop:text-[24px] uppercase">
                    {benefitCards[5].title}
                  </p>
                  <p className="w-fit font-sans text-[16px] leading-[20px] laptop:text-[20px] laptop:leading-[32px]">
                    {benefitCards[5].description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-[38px] w-full max-w-screen-desktop-l mx-auto z-1 flex justify-center tablet:justify-end">
          <div
            className="relative w-full max-w-[298px] tablet:max-w-[428px]"
            style={{ height: noteBgHeight }}
          >
            <img
              src={NoteBg.src}
              alt="Note Background"
              className="block tablet:hidden absolute inset-0 w-full h-full object-fill z-0"
            />
            <img
              src={NoteBgDesktop.src}
              alt="Note Background"
              className="hidden tablet:block absolute inset-0 w-full h-full object-fill z-0"
            />
            <div
              ref={noteTextRef}
              className="absolute top-[20%] tablet:top-[25%] left-[8%] tablet:left-[5%] w-[85%] mx-auto pointer-events-none select-none z-10"
            >
              <p className="w-fit rotate-[-3deg] text-[16px] tablet:text-[18px] text-[rgba(255,255,255,0.5)] font-williamItalic italic leading-[18px]">
                {noteHeading}
              </p>
              <p className="w-fit rotate-[-3deg] mb-0 font-williamItalic italic text-white text-left text-[16px] leading-[20px] tablet:text-[18px] laptop:leading-[28px]">
                {noteText}
              </p>
            </div>
          </div>
        </div>
        <BottomBgMobile
          viewBox="0 0 525 96"
          className="absolute bottom-[-1px] left-0 w-full pointer-events-none select-none"
        />
      </section>
    </>
  )
}

const BenefitsComponent = observer(Benefits)

export { BenefitsComponent }
